import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { API } from "@/lib/api";
import Lightbox from "@/components/Lightbox";

const VARIANT_ACCENTS = { kl: "#00E676", dr: "#2997FF", golden_raja: "#FFD700" };

export default function TodayStrip() {
  const [charts, setCharts] = useState([]);
  const [isToday, setIsToday] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    const load = () => {
      api
        .get("/charts")
        .then((res) => {
          const all = res.data;
          const todayKey = new Date().toLocaleDateString("en-CA");
          const todays = all.filter((c) => new Date(c.created_at).toLocaleDateString("en-CA") === todayKey);
          if (todays.length) {
            setCharts(todays);
            setIsToday(true);
          } else {
            setCharts(all.slice(0, 10));
            setIsToday(false);
          }
        })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 180000);
    return () => clearInterval(t);
  }, []);

  if (!charts.length) return null;

  return (
    <section data-testid="today-strip" className="px-6 py-24 md:px-16 lg:px-24">
      <div className="mx-auto max-w-[1600px]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          className="mb-10 flex items-end justify-between"
        >
          <div>
            <p className="mb-3 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#666666]">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#FFD700]" />
              {isToday ? "Fresh today" : "Latest drops"}
            </p>
            <h2 className="font-heading text-4xl font-black uppercase tracking-tighter md:text-6xl">
              {isToday ? "Today's charts" : "Latest charts"}
            </h2>
          </div>
          <p className="hidden font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666] md:block">
            Scroll →
          </p>
        </motion.div>

        <div className="no-scrollbar -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-4 md:gap-6">
          {charts.map((chart, i) => (
            <motion.button
              key={chart.id}
              data-testid={`today-chart-card-${chart.id}`}
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.76, 0, 0.24, 1] }}
              onClick={() => setActive(chart)}
              className="group w-40 shrink-0 snap-start overflow-hidden rounded-lg border border-white/10 bg-[#0A0A0A] text-left transition-all duration-300 hover:-translate-y-1 hover:border-white/30 md:w-52"
            >
              <div
                className="h-1 w-full"
                style={{ backgroundColor: VARIANT_ACCENTS[chart.variant] || "#FFD700" }}
              />
              <div className="aspect-[3/4] overflow-hidden bg-black">
                <img
                  src={`${API}/files/${chart.url.replace("/api/files/", "")}`}
                  alt={chart.title}
                  loading="lazy"
                  className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-3">
                <p className="truncate text-xs font-semibold">{chart.title}</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-[#666666]">
                  {chart.variant_label}
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <Lightbox chart={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
